import { createContext, use, type ReactNode } from "react";
import { useTranslations } from "../i18n";
import type {
  TransferListItem,
  TransferListPanelConfig,
  TransferListProps,
} from "./types";
import { useTransferListState } from "./useTransferListState";

type TransferListContextValue<T = unknown> = TransferListProps<T> & {
  sourceSearch: string;
  targetSearch: string;
  filteredSourceItems: TransferListItem<T>[];
  filteredTargetItems: TransferListItem<T>[];
  unselectedItems: TransferListItem<T>[];
  setSourceSearch: (value: string) => void;
  setTargetSearch: (value: string) => void;
  addItem: (item: TransferListItem<T>) => void;
  removeItem: (item: TransferListItem<T>) => void;
  addAll: () => void;
  removeAll: () => void;
  sourceConfig: TransferListPanelConfig;
  targetConfig: TransferListPanelConfig;
};

const TransferListContext = createContext<TransferListContextValue | null>(
  null,
);

export function useTransferListContext<T = unknown>() {
  const context = use(TransferListContext);
  if (!context) {
    throw new Error(
      "useTransferListContext must be used within TransferListProvider",
    );
  }
  return context as TransferListContextValue<T>;
}

type TransferListProviderProps<T> = TransferListProps<T> & {
  children: ReactNode;
};

export function TransferListProvider<T>(props: TransferListProviderProps<T>) {
  const { children, ...rest } = props;
  const transferListProps = rest as TransferListProps<T>;

  const { t } = useTranslations();
  const state = useTransferListState<T>(transferListProps);

  const {
    selectedItems,
    searchEnabled = true,
    showBulkActions = true,
    loading = false,
    disabled = false,
    renderItem,
  } = transferListProps;

  const sourceConfig: TransferListPanelConfig = {
    title:
      transferListProps.sourceConfig?.title ??
      t("components.transferList.availableItems"),
    countLabel: transferListProps.sourceConfig?.countLabel,
    searchPlaceholder:
      transferListProps.sourceConfig?.searchPlaceholder ??
      t("components.transferList.searchPlaceholder"),
    emptyMessage: transferListProps.sourceConfig?.emptyMessage,
  };

  const targetConfig: TransferListPanelConfig = {
    title:
      transferListProps.targetConfig?.title ??
      t("components.transferList.selectedItems"),
    countLabel: transferListProps.targetConfig?.countLabel,
    searchPlaceholder:
      transferListProps.targetConfig?.searchPlaceholder ??
      t("components.transferList.searchPlaceholder"),
    emptyMessage: transferListProps.targetConfig?.emptyMessage,
  };

  const contextValue: TransferListContextValue<T> = {
    ...transferListProps,
    ...state,
    selectedItems,
    sourceConfig,
    targetConfig,
    searchEnabled,
    showBulkActions,
    loading,
    disabled,
    renderItem,
  };

  return (
    <TransferListContext value={contextValue as TransferListContextValue}>
      {children}
    </TransferListContext>
  );
}
